
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { NotificationSettings, NotificationSettingsForm } from './types';

export async function fetchNotificationSettings(userId: string): Promise<NotificationSettings | null> {
  try {
    const { data, error } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching notification settings:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in fetchNotificationSettings:', error);
    return null;
  }
}

export async function updateNotificationSettings(
  userId: string,
  settings: NotificationSettingsForm
): Promise<NotificationSettings | null> {
  try {
    console.log('Updating notification settings for userId:', userId);
    
    // First, check if notification settings exist
    const { data: existingSettings } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (!existingSettings) {
      console.log('No existing notification settings found, creating new settings');
      
      // Get the current authenticated user for RLS policy
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || user.id !== userId) {
        console.error('Authentication mismatch: current user does not match target user ID');
        toast.error('Authentication error. Please log in again.');
        return null;
      }
      
      // Insert new settings
      const { data: insertedData, error: insertError } = await supabase
        .from('notification_settings')
        .insert({
          user_id: user.id,
          email_orders: settings.email.orders,
          email_marketing: settings.email.marketing,
          email_updates: settings.email.updates,
          push_orders: settings.push.orders,
          push_marketing: settings.push.marketing,
          push_updates: settings.push.updates
        })
        .select()
        .single();
      
      if (insertError) {
        console.error('Error creating notification settings:', insertError);
        toast.error('Failed to create notification preferences');
        return null;
      }
      
      toast.success('Notification preferences created successfully');
      return insertedData;
    }
    
    // Update existing settings
    console.log('Existing notification settings found, updating');
    const { data, error } = await supabase
      .from('notification_settings')
      .update({
        email_orders: settings.email.orders,
        email_marketing: settings.email.marketing,
        email_updates: settings.email.updates,
        push_orders: settings.push.orders,
        push_marketing: settings.push.marketing,
        push_updates: settings.push.updates
      })
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification preferences');
      return null;
    }
    
    toast.success('Notification preferences updated successfully');
    return data;
  } catch (error) {
    console.error('Error in updateNotificationSettings:', error);
    toast.error('An unexpected error occurred');
    return null;
  }
}
